import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Plus, AlertCircle, Clock, Circle, Download, FileText, Calendar, Briefcase, BookOpen, Lightbulb } from 'lucide-react';
import { WorkTable } from './components/WorkTable/index';
import { AddItemModal } from './components/Modals';
import { SettingsMenu } from './components/SettingsMenu';
import { NotesView } from './components/Notes/NotesView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingOverlay, TableSkeleton } from './components/Loading';
import { db } from './services/database';
import { WorkItem } from './types';
import { migrateDescriptionsToTags } from './utils/tagMigration';
import { styleDefinitions as styles } from './theme/styles';
import { useNetworkStatus } from './hooks/useNetworkStatus';
// import './App.css'; // REMOVED - Using centralized styles.ts only

// Lazy load heavy modals
const CategoryManagementModal = lazy(() => import('./components/Modals').then(m => ({ default: m.CategoryManagementModal })));

function AppContent() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [parentCategory, setParentCategory] = useState<{ level1: string; level2: string } | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  const isOnline = useNetworkStatus();
  // Direct use of Dexie's reactive query - no stabilization needed
  const workItems = useLiveQuery(() => db.getAllWorkItems(), [refreshKey]);

  // Set loading to false once workItems is loaded
  useEffect(() => {
    if (workItems !== undefined) {
      setIsLoading(false);
    }
  }, [workItems]);

  // Update storage information
  const updateStorageInfo = useCallback(async () => {
    try {
      const estimate = await db.getStorageEstimate();
      console.log(`Storage usage: ${estimate.percentage.toFixed(2)}%`);
    } catch (error) {
      console.error('Failed to get storage estimate:', error);
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await updateStorageInfo();
      } catch (error) {
        console.error('Failed to update storage info:', error);
      }
    };

    initializeApp();

    // Define the auto-save interval
    const interval = setInterval(() => {
      // Call updateStorageInfo directly instead of autoSave to avoid reference issues
      updateStorageInfo().catch(error => {
        console.error('Auto-save failed:', error);
      });
    }, 30000);

    // Run migration once on app load if not already done
    const migrationKey = 'focusHubTagMigrationV3';
    if (!localStorage.getItem(migrationKey)) {
      console.log('Running tag migration...');
      migrateDescriptionsToTags();
      localStorage.setItem(migrationKey, 'completed');
    }

    return () => {
      clearInterval(interval);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, [updateStorageInfo]);

  // Timer effect for current time
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    // Load custom categories from localStorage
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    if (savedCategories) {
      try {
        const parsed = JSON.parse(savedCategories);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCustomCategories(parsed);
        }
      } catch (e) {
        console.error('Failed to parse saved categories');
      }
    }
  }, []);



  const handleAddItem = useCallback(async (item: WorkItem) => {
    setIsSaving(true);
    try {
      await db.saveWorkItem(item);
      setShowAddModal(false);
      setParentCategory(undefined);
      showSuccess('Item added successfully');
    } catch (err) {
      showError('Failed to add item');
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleUpdateItem = useCallback(async (item: WorkItem) => {
    setIsSaving(true);
    try {
      await db.saveWorkItem(item);
    } catch (err) {
      showError('Failed to update item');
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleDeleteItem = useCallback(async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setIsDeleting(true);
      try {
        await db.deleteWorkItem(id);
        showSuccess('Item deleted successfully');
      } catch (err) {
        showError('Failed to delete item');
      } finally {
        setIsDeleting(false);
      }
    }
  }, []);

  const handleExport = async () => {
    try {
      const data = await db.exportToJSON();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clean-room-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess('Data exported successfully');
    } catch (err) {
      showError('Failed to export data');
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const text = await file.text();
          await db.importFromJSON(text);
          showSuccess('Data imported successfully');
          updateStorageInfo();
        } catch (err) {
          showError('Failed to import data. Please check the file format.');
        }
      }
    };
    input.click();
  };

  const showError = (message: string) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setError(message);
    errorTimeoutRef.current = setTimeout(() => {
      setError(null);
      errorTimeoutRef.current = null;
    }, 5000);
  };

  const showSuccess = (message: string) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setSuccess(message);
    successTimeoutRef.current = setTimeout(() => {
      setSuccess(null);
      successTimeoutRef.current = null;
    }, 3000);
  };


  const formatLocalTime = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weekday = weekdays[date.getDay()];
    return `${year}/${month}/${day} ${weekday}`;
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const outcome = await deferredPrompt.userChoice;

    if (outcome.outcome === 'accepted') {
      showSuccess('App installed successfully!');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  return (
    <div style={styles.combine(
      styles.size.minHScreen,
      { background: 'var(--color-background)' }
    )}>
      {/* Subtle dot pattern background */}
      <div className="app-background" />
      <div className="dot-pattern" />
      
      <header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '64px'
          }}>
            <button
              onClick={() => {
                // Always return to the default grouped view (tree view)
                if (showNotes) {
                  setShowNotes(false);
                }
                // Set the default tree view mode
                localStorage.setItem('cleanRoomViewMode', 'tree');
                // Trigger a refresh to reset the view
                setRefreshKey(prev => prev + 1);
              }}
              style={{
                ...styles.button.transparent,
                gap: '16px'
              }}
              title="Return to Work Items"
            >
              {/* Logo Icon */}
              <div style={{
                position: 'relative',
                width: '32px',
                height: '32px',
                background: '#3b82f6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Circle
                  size={20}
                  strokeWidth={2.5}
                  style={{
                    color: 'white'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'white'
                }}></div>
              </div>

              {/* Enhanced Branding */}
              <div>
                <h1 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#111827',
                  letterSpacing: '-0.5px',
                  margin: 0,
                  lineHeight: '1.2'
                }}>
                  Focus Hub
                </h1>
                <p style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  fontWeight: '400',
                  margin: 0,
                  marginTop: '2px'
                }}>
                  Organize & Focus
                </p>
              </div>
            </button>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {/* Enhanced Status Indicators */}
              {window.innerWidth >= 1024 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginRight: '8px'
                }}>
                  <div style={styles.components.localDateDisplay.container}>
                    <Calendar size={14} style={styles.components.localDateDisplay.icon} />
                    <span style={styles.components.localDateDisplay.text}>
                      {formatLocalDate(currentTime)}
                    </span>
                  </div>

                  <div style={styles.components.localTimeDisplay.container}>
                    <Clock size={14} style={styles.components.localTimeDisplay.icon} />
                    <span style={styles.components.localTimeDisplay.text}>
                      {formatLocalTime(currentTime)}
                    </span>
                  </div>
                </div>
              )}

              {/* Premium Action Buttons */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <button
                  onClick={() => setShowAddModal(true)}
                  style={styles.button.primaryGradientLarge}
                >
                  <Plus size={18} strokeWidth={2.5} />
                  {window.innerWidth >= 640 && (
                    <span>Add Item</span>
                  )}
                </button>

                <button
                  onClick={() => setShowNotes(!showNotes)}
                  style={showNotes ? styles.button.primaryGradientLarge : styles.layout.headerButtonSecondary}
                >
                  <FileText size={18} strokeWidth={2} />
                  {window.innerWidth >= 640 && (
                    <span>Notes</span>
                  )}
                </button>

                {showInstallPrompt && (
                  <button
                    onClick={handleInstallClick}
                    style={{
                      ...styles.button.primaryGradientLarge,
                      animation: 'pulse 2s infinite'
                    }}
                    title="Install Focus Hub as an app"
                  >
                    <Download size={18} strokeWidth={2} />
                    {window.innerWidth >= 640 && (
                      <span>Install App</span>
                    )}
                  </button>
                )}

                <SettingsMenu
                  onManageCategories={() => setShowCategoryModal(true)}
                  onExport={handleExport}
                  onImport={handleImport}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {!isOnline && (
        <div style={{
          background: 'linear-gradient(to right, #fef3c7, #fde68a)',
          padding: '8px',
          textAlign: 'center',
          borderBottom: '1px solid #f59e0b'
        }}>
          <span style={{ color: '#92400e', fontWeight: 500 }}>
            🔌 Offline Mode - Changes will be saved locally
          </span>
        </div>
      )}

      {error && (
        <div style={styles.combine(
          styles.layout.maxWidth,
          styles.spacing.marginHorizontalAuto,
          styles.spacing.px4,
          styles.spacing.marginTopLg,
          styles.animation.fadeIn
        )}>
          <div style={styles.combine(
            styles.utility.roundedLg,
            styles.spacing.paddingLg,
            styles.layout.flex,
            styles.spacing.gap3,
            styles.utility.shadow,
            {
              alignItems: 'center',
              background: 'linear-gradient(to right, #fef2f2, #fff1f2)',
              border: '1px solid rgba(254, 202, 202, 0.5)'
            }
          )}>
            <div style={styles.combine(
              styles.spacing.paddingSm,
              styles.utility.roundedLg,
              { background: '#fee2e2' }
            )}>
              <AlertCircle className="text-red-600" size={20} />
            </div>
            <span style={styles.combine(
              styles.typography.fontMedium,
              { color: '#991b1b' }
            )}>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div style={styles.combine(
          styles.layout.maxWidth,
          styles.spacing.marginHorizontalAuto,
          styles.spacing.px4,
          styles.spacing.marginTopLg,
          styles.animation.fadeIn
        )}>
          <div style={styles.combine(
            styles.utility.roundedLg,
            styles.spacing.paddingLg,
            styles.utility.shadow,
            {
              background: 'linear-gradient(to right, #f0fdf4, #dcfce7)',
              border: '1px solid rgba(134, 239, 172, 0.5)'
            }
          )}>
            <span style={styles.combine(
              styles.typography.fontMedium,
              styles.layout.flex,
              styles.spacing.gap2,
              {
                alignItems: 'center',
                color: '#166534'
              }
            )}>
              <span style={{ color: '#16a34a' }}>✓</span> {success}
            </span>
          </div>
        </div>
      )}

      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 24px 80px',
        position: 'relative',
        zIndex: 1,
        background: '#f8fafc',
        minHeight: '100vh'
      }}>
        {showNotes ? (
          <div style={styles.components.notes.whiteboard.wrapper}>
            <NotesView onBack={() => setShowNotes(false)} />
          </div>
        ) : workItems && workItems.length === 0 ? (
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            borderRadius: '32px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 32px 64px rgba(59, 130, 246, 0.15)',
            padding: '80px 60px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeIn 600ms ease-out'
          }}>
            {/* Animated background elements */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(37, 99, 235, 0.05) 0%, transparent 50%)',
              animation: 'pulse 4s ease-in-out infinite',
              zIndex: -1
            }}></div>


            {/* Hero Text */}
            <h2 style={{
              fontSize: '42px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #1f2937 0%, #3b82f6 50%, #2563eb 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-1px',
              lineHeight: '1.1',
              marginBottom: '16px',
              margin: '0 auto 16px'
            }}>
              Welcome to Focus Hub
            </h2>

            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              fontWeight: '400',
              lineHeight: '1.6',
              maxWidth: '520px',
              margin: '0 auto 48px',
              letterSpacing: '-0.1px'
            }}>
              Your personal Focus Hub to organize your items for work, learning, and interests, 100% local storage
            </p>

            {/* Premium CTA Button */}
            <button
              onClick={() => setShowAddModal(true)}
              style={{
                ...styles.button.hero,
                marginBottom: '80px'
              }}
            >
              <Plus size={22} strokeWidth={2.5} />
              Create Your First Item
            </button>

            {/* Feature Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              <div style={{
                padding: '32px 24px',
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                textAlign: 'left',
                cursor: 'pointer'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  ...styles.extended.gradientGreen,
                  marginBottom: '20px',
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)'
                }}>
                  <Briefcase size={24} style={{ color: 'white' }} />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '8px',
                  letterSpacing: '-0.3px'
                }}>
                  What I am working on
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Track your active projects, tasks, and professional goals in one organized space.
                </p>
              </div>

              <div style={{
                padding: '32px 24px',
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                textAlign: 'left',
                cursor: 'pointer'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  ...styles.extended.gradientBlue,
                  marginBottom: '20px',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                }}>
                  <BookOpen size={24} style={{ color: 'white' }} />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '8px',
                  letterSpacing: '-0.3px'
                }}>
                  What I am learning
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Organize your courses, study materials, and skill development journey.
                </p>
              </div>

              <div style={{
                padding: '32px 24px',
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                textAlign: 'left',
                cursor: 'pointer'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  ...styles.extended.gradientYellow,
                  marginBottom: '20px',
                  boxShadow: '0 8px 20px rgba(251, 191, 36, 0.3)'
                }}>
                  <Lightbulb size={24} style={{ color: 'white' }} />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '8px',
                  letterSpacing: '-0.3px'
                }}>
                  What I am interested
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Capture ideas, inspirations, and topics you want to explore in the future.
                </p>
              </div>
            </div>
          </div>
        ) : isLoading || !workItems ? (
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            borderRadius: '32px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 32px 64px rgba(59, 130, 246, 0.1)',
            padding: '60px',
            animation: 'fadeIn 400ms ease-out'
          }}>
            <TableSkeleton rows={8} columns={5} />
          </div>
        ) : (
          <ErrorBoundary level="section">
            <LoadingOverlay isLoading={isSaving || isDeleting} message={isSaving ? 'Saving...' : 'Deleting...'}>
              <div style={styles.table.mainContainer}>
                <WorkTable
                  data={workItems}
                  onUpdate={handleUpdateItem}
                  onDelete={handleDeleteItem}
                  onAdd={(parentItem) => {
                    if (parentItem) {
                      setParentCategory({
                        level1: parentItem.level1,
                        level2: parentItem.level2 || 'Items'
                      });
                    }
                    setShowAddModal(true);
                  }}
                  refreshKey={refreshKey}
                />
              </div>
            </LoadingOverlay>
          </ErrorBoundary>
        )}
      </main>

      {showAddModal && (
        <AddItemModal
          parentCategory={parentCategory}
          onSave={handleAddItem}
          onClose={() => {
            setShowAddModal(false);
            setParentCategory(undefined);
          }}
          customCategories={customCategories}
          onOpenCategoryManagement={() => {
            setShowAddModal(false);
            setShowCategoryModal(true);
          }}
        />
      )}

      {showCategoryModal && (
        <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}>
          <CategoryManagementModal
            onClose={() => setShowCategoryModal(false)}
            onCategoryUpdate={() => {
              // Reload categories from localStorage
              const savedCategories = localStorage.getItem('cleanRoomCategories');
              if (savedCategories) {
                try {
                  const parsed = JSON.parse(savedCategories);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    setCustomCategories(parsed);
                  }
                } catch (e) {
                  console.error('Failed to parse saved categories');
                }
              }
              // Trigger a refresh of the data
              setRefreshKey(prev => prev + 1);
            }}
          />
        </Suspense>
      )}

    </div>
  );
}

export default AppContent;